import { useState, useEffect, useCallback, useRef } from 'react';
import { getAssociateData, sospensionePushInCorso } from '../services/otpAuthService';
import { eDipendente, idPersonaChat, sessioneDiDipendente } from '../utils/sezioniDipendente';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || '';

/**
 * Converte la chiave VAPID pubblica da base64url a Uint8Array
 * (necessario per la Push API del browser)
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export type PushPermissionState = 'default' | 'granted' | 'denied' | 'unsupported';

/**
 * Prima del subscribe si aspettano, al più 5 s, le operazioni sull'iscrizione ancora in volo che la
 * riguardano per tutto lo studio: la sospensione del logout appena fatto e il ricollegamento della persona
 * di prima (altro token). Altrimenti, arrivando al server dopo il subscribe di chi entra, gli toglierebbero
 * o riprenderebbero l'iscrizione appena presa.
 * `pronto` risolve con true se si è partiti per il tetto dei 5 s (operazioni ancora aperte); `chiuse`
 * risolve quando si sono chiuse tutte (mai rifiutata).
 */
function dopoLeOperazioniInCorso(precedente: Promise<unknown> | null): { pronto: Promise<boolean>; chiuse: Promise<void> } {
  const attese = [sospensionePushInCorso(), precedente].filter((p): p is Promise<unknown> => !!p);
  if (!attese.length) return { pronto: Promise.resolve(false), chiuse: Promise.resolve() };
  let timer: ReturnType<typeof setTimeout> | undefined;
  const limite = new Promise<boolean>((r) => { timer = setTimeout(() => r(true), 5000); });
  const chiuse = Promise.all(attese.map((p) => p.then(() => undefined, () => undefined))).then(() => undefined);
  const pronto = Promise.race([chiuse.then(() => false), limite]).then((scaduto) => {
    if (timer) clearTimeout(timer);
    return scaduto;
  });
  return { pronto, chiuse };
}

/**
 * Ricollegamento all'avvio dell'iscrizione già presente nel browser (POST /push/subscribe), condiviso
 * tra le istanze dell'hook (la campanella è montata sia in Header sia in MobileMenu): una sola chiamata
 * per coppia token + endpoint. Un nuovo login cambia il token e quindi richiama il server.
 * Risolve con l'id della persona a cui il server ha legato l'iscrizione (il dipendente con `dipendente`
 * nella risposta, altrimenti l'associato, cioè il titolare), o null se l'ha rifiutata.
 * Resta in memoria solo l'esito definitivo (persona, 401, 403): un errore del server o di rete si
 * dimentica e il montaggio successivo ritenta.
 * Se il subscribe è partito per il tetto dei 5 s, l'operazione precedente può arrivare al server dopo di
 * lui e togliere o riprendere l'iscrizione: quando si chiude (e si è chiuso anche il subscribe), l'esito
 * della chiave corrente si dimentica e il ricollegamento si rifà, se nel frattempo non è entrato nessun altro.
 */
let ricollegamento: { chiave: string; promessa: Promise<string | null> } | null = null;

function ricollegaIscrizione(token: string, subscription: PushSubscription): Promise<string | null> {
  const chiave = `${token}|${subscription.endpoint}`;
  if (ricollegamento && ricollegamento.chiave === chiave) return ricollegamento.promessa;
  const precedente = ricollegamento ? ricollegamento.promessa : null;
  const dimentica = () => {
    if (ricollegamento && ricollegamento.chiave === chiave) ricollegamento = null;
  };
  const attesa = dopoLeOperazioniInCorso(precedente);
  const promessa = attesa.pronto
    .then(() => fetch(`${API_BASE_URL}/push/subscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ subscription: subscription.toJSON() })
    }))
    .then(async (response) => {
      if (!response.ok) {
        if (response.status !== 401 && response.status !== 403) dimentica();
        return null;
      }
      const body = await response.json();
      return body && body.success && body.associato ? String(body.dipendente || body.associato) : null;
    })
    .catch((err) => {
      // Errore di rete: il prossimo montaggio ritenta
      dimentica();
      throw err;
    });
  ricollegamento = { chiave, promessa };
  attesa.pronto.then((scaduto) => {
    if (!scaduto) return;
    Promise.all([attesa.chiuse, promessa.catch(() => null)]).then(() => {
      if (!ricollegamento || ricollegamento.chiave !== chiave) return;
      ricollegamento = null;
      // Anche la sospensione del dipendente con la stessa chiave va potuta ripetere
      if (sospensioneDipendente === chiave) sospensioneDipendente = null;
      ricollegaOSospendi(token, subscription, tokenDiDipendente(token)).catch(() => undefined);
    });
  });
  return promessa;
}

/** Solo per i test: dimentica il ricollegamento in memoria. */
export function __azzeraRicollegamentoPush(): void {
  ricollegamento = null;
}

/**
 * Ripiego del browser condiviso (piano 2): sospende l'iscrizione push del browser (POST /push/sospendi
 * col token del dipendente: il server accetta solo un endpoint di una persona dello stesso studio), così
 * il dipendente non vede le notifiche del titolare. Dal piano 3 si usa solo quando l'iscrizione non è
 * passata al dipendente (qualunque esito del subscribe diverso da «persona = me», o permesso non più
 * concesso, vedi `ricollegaOSospendi`); un subscribe riuscito più avanti la riattiva.
 * Una sola chiamata per coppia token + endpoint; mai lancia.
 */
let sospensioneDipendente: string | null = null;

export async function sospendiPushDipendente(tokenDato?: string | null): Promise<void> {
  try {
    const token = tokenDato || localStorage.getItem('associate_token');
    if (!token || typeof navigator === 'undefined' || !navigator.serviceWorker?.getRegistration) return;
    const registration = await navigator.serviceWorker.getRegistration();
    const subscription = await registration?.pushManager?.getSubscription();
    if (!subscription?.endpoint) return;
    const chiave = `${token}|${subscription.endpoint}`;
    if (sospensioneDipendente === chiave) return;
    sospensioneDipendente = chiave;
    const response = await fetch(`${API_BASE_URL}/push/sospendi`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ endpoint: subscription.endpoint })
    });
    // 404: l'iscrizione non è dello studio (nulla da sospendere); un errore del server si ritenta al prossimo avvio
    if (!response.ok && response.status >= 500 && sospensioneDipendente === chiave) sospensioneDipendente = null;
  } catch (err) {
    sospensioneDipendente = null;
  }
}

/** Solo per i test: dimentica la sospensione già chiesta dal dipendente. */
export function __azzeraSospensioneDipendente(): void {
  sospensioneDipendente = null;
}

/** Payload (non verificato) del token, o null se illeggibile. */
function payloadDi(token: string): Record<string, unknown> | null {
  try {
    const base64 = (token.split('.')[1] || '').replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64 + '='.repeat((4 - (base64.length % 4)) % 4)));
    return payload && typeof payload === 'object' ? payload : null;
  } catch (e) {
    return null;
  }
}

/**
 * Il token è di un dipendente? Dal campo `type` del token stesso (quello che si manda al server), non dai
 * dati salvati letti al disegno; solo se il token non si legge decide `sessioneDiDipendente`.
 */
function tokenDiDipendente(token: string): boolean {
  const payload = payloadDi(token);
  if (payload && typeof payload.type === 'string') return payload.type === 'dipendente';
  return sessioneDiDipendente();
}

/**
 * Id della persona del token: il dipendente (`idDipendenteDi`) o il titolare (`associateId` del token; se il
 * token non si legge, l'id dei dati salvati come prima). Stringa vuota se non si sa.
 */
function idPersonaDelToken(token: string, dipendente: boolean): string {
  if (dipendente) return idDipendenteDi(token);
  const payload = payloadDi(token);
  if (payload && payload.associateId) return String(payload.associateId);
  try {
    return idPersonaChat(getAssociateData());
  } catch (e) {
    return '';
  }
}

/**
 * Id del dipendente a cui deve passare l'iscrizione: dal token (`dipendenteId`) o, se il token non si legge,
 * dai dati salvati del dipendente. Stringa vuota se non si sa (mai l'id dello studio al suo posto).
 */
function idDipendenteDi(token: string): string {
  const payload = payloadDi(token);
  if (payload && payload.type === 'dipendente' && payload.dipendenteId) return String(payload.dipendenteId);
  try {
    const dati = getAssociateData();
    return eDipendente(dati) ? idPersonaChat(dati) : '';
  } catch (e) {
    return '';
  }
}

/**
 * Iscrizione già presente nel browser ⇒ alla persona del token (N5: il browser passa all'ultima persona
 * che entra). Con il permesso concesso: POST /push/subscribe. Il titolare resta come prima (senza permesso
 * nulla, nessuna sospensione dal sito; un errore di rete si propaga). Per il dipendente l'iscrizione non deve
 * mai restare a un altro (il titolare, se la sospensione del suo logout non è arrivata): su qualunque esito
 * diverso da «persona = me» (rifiuto, errore del server o di rete, risposta senza il mio id) o senza
 * permesso, si sospende. Risolve con l'id della persona a cui il server l'ha legata, o null.
 */
async function ricollegaOSospendi(token: string, subscription: PushSubscription, dipendente: boolean): Promise<string | null> {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
    if (dipendente) await sospendiPushDipendente(token);
    return null;
  }
  if (!dipendente) return ricollegaIscrizione(token, subscription);
  const mio = idDipendenteDi(token);
  let persona: string | null = null;
  try {
    persona = await ricollegaIscrizione(token, subscription);
  } catch (err) {
    persona = null;
  }
  if (!mio || persona !== mio) {
    await sospendiPushDipendente(token);
    return null;
  }
  return persona;
}

/**
 * Subito dopo il login (o il ponte dall'app) del dipendente: l'iscrizione del browser passa a lui col
 * token appena ricevuto (vedi `ricollegaOSospendi`). L'avvio della sessione ripete lo stesso controllo,
 * ma con lo stesso token e lo stesso endpoint riusa l'esito senza nuove chiamate. Mai lancia.
 */
export async function ricollegaPushDipendente(tokenDato?: string | null): Promise<void> {
  try {
    const token = tokenDato || localStorage.getItem('associate_token');
    if (!token || typeof navigator === 'undefined' || !navigator.serviceWorker?.getRegistration) return;
    const registration = await navigator.serviceWorker.getRegistration();
    const subscription = await registration?.pushManager?.getSubscription();
    if (!subscription?.endpoint) return;
    await ricollegaOSospendi(token, subscription, true);
  } catch (err) {
    // service worker non raggiungibile: si ritenta all'avvio della sessione (il ricollegamento non lancia)
  }
}

/**
 * Nuovo tentativo del ricollegamento dell'iscrizione del browser alla persona del token attuale (vedi
 * `ricollegaOSospendi`): al ritorno della rete (evento `online`) e alla riconnessione del socket `/notifications`.
 * Serve al cambio di persona fatto senza rete (titolare che esce e dipendente che entra, o viceversa): ricollegamento
 * e sospensione falliti per rete non restano sospesi fino a un ricaricamento. Stessa chiave token + endpoint: se
 * l'esito c'è già, nessuna nuova chiamata. Non chiede permessi né crea iscrizioni. Risolve con l'id della persona a
 * cui il server ha legato l'iscrizione, o null; mai lancia.
 */
export async function ritentaRicollegamentoPush(): Promise<string | null> {
  try {
    const token = localStorage.getItem('associate_token');
    if (!token || typeof navigator === 'undefined' || !navigator.serviceWorker?.getRegistration) return null;
    const registration = await navigator.serviceWorker.getRegistration();
    const subscription = await registration?.pushManager?.getSubscription();
    if (!subscription?.endpoint) return null;
    return await ricollegaOSospendi(token, subscription, tokenDiDipendente(token));
  } catch (err) {
    return null;
  }
}

let risottoscrizione: Promise<boolean> | null = null;

/** Token di «Accedi come associato» (`impersonatedBy` nel payload, letto senza verifica; illeggibile: decide il server). */
function tokenInImpersonificazione(token: string): boolean {
  const payload = payloadDi(token);
  return !!(payload && payload.impersonatedBy);
}

/**
 * Rinnovo dell'iscrizione chiesto dal service worker (`pushsubscriptionchange` ⇒ messaggio
 * `risottoscrivi`, notifiche chat fase A §10): il service worker non ha il token, la pagina sì.
 * Con permesso concesso e token associato (titolare o dipendente): l'iscrizione attuale del browser (o
 * una nuova, se il browser l'ha revocata) si ricollega alla persona del token con la stessa strada
 * dell'avvio (POST /push/subscribe). Una sola esecuzione alla volta. Risolve true se il server l'ha
 * legata a una persona; mai lancia. In impersonificazione non fa nulla (nemmeno un'iscrizione nuova nel browser).
 */
export function risottoscriviPush(): Promise<boolean> {
  if (risottoscrizione) return risottoscrizione;
  risottoscrizione = (async () => {
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return false;
      if (Notification.permission !== 'granted') return false;
      const token = localStorage.getItem('associate_token');
      if (!token || tokenInImpersonificazione(token)) return false;
      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) return false;
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        if (!VAPID_PUBLIC_KEY) return false;
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
        });
      }
      return !!(await ricollegaOSospendi(token, subscription, tokenDiDipendente(token)));
    } catch (err) {
      console.warn('Rinnovo dell\'iscrizione push non riuscito:', err);
      return false;
    }
  })().finally(() => {
    risottoscrizione = null;
  });
  return risottoscrizione;
}

interface UsePushNotificationsReturn {
  /** Stato del permesso browser per le notifiche */
  permission: PushPermissionState;
  /** Se l'utente è attualmente iscritto alle push */
  isSubscribed: boolean;
  /** Se un'operazione è in corso */
  loading: boolean;
  /** Errore dell'ultima operazione */
  error: string | null;
  /** Se il browser supporta le push notifications */
  isSupported: boolean;
  /** Richiedi permesso e iscriviti alle push */
  subscribe: () => Promise<boolean>;
  /** Disiscriviti dalle push */
  unsubscribe: () => Promise<boolean>;
}

/**
 * Hook per gestire le Web Push Notifications
 * 
 * Gestisce: registrazione Service Worker, richiesta permessi browser,
 * creazione subscription e comunicazione con il backend.
 */
export const usePushNotifications = (): UsePushNotificationsReturn => {
  const [permission, setPermission] = useState<PushPermissionState>('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const swRegistrationRef = useRef<ServiceWorkerRegistration | null>(null);

  // Dipendente dello studio (piano 3): push web sue, con l'interruttore come il titolare. Qui serve solo a
  // rifare il controllo se cambia la persona con l'hook montato: chi è dipendente si legge dal token usato
  // (`tokenDiDipendente`), nello stesso punto in cui lo si manda.
  const dipendente = sessioneDiDipendente();
  const isSupported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

  // Controlla stato iniziale
  useEffect(() => {
    if (!isSupported) {
      setPermission('unsupported');
      return;
    }

    // Leggi permesso attuale
    setPermission(Notification.permission as PushPermissionState);

    let attivo = true;

    // Registra SW e controlla subscription esistente. Nessuno fa logout: l'iscrizione del browser resta
    // e va ricollegata alla persona del login (e riattivata se il logout l'aveva sospesa), sempre.
    navigator.serviceWorker.register('/sw.js')
      .then((registration) => {
        swRegistrationRef.current = registration;
        return registration.pushManager.getSubscription();
      })
      .then(async (subscription) => {
        const token = localStorage.getItem('associate_token');
        if (!subscription || !token) {
          if (attivo) setIsSubscribed(false);
          return;
        }
        const delDipendente = tokenDiDipendente(token);
        const persona = await ricollegaOSospendi(token, subscription, delDipendente);
        // Iscritto solo se il server l'ha legata a me (per il dipendente il suo id, non quello dello studio)
        const mio = idPersonaDelToken(token, delDipendente);
        if (attivo) setIsSubscribed(!!persona && (!mio || mio === persona));
      })
      .catch((err) => {
        if (attivo) setIsSubscribed(false);
        console.warn('Errore registrazione Service Worker o iscrizione push:', err);
      });

    // Rete tornata: si ritenta (il ricollegamento fallito per rete non resta in memoria, quello riuscito sì)
    const alRitornoDellaRete = () => {
      const token = localStorage.getItem('associate_token');
      ritentaRicollegamentoPush().then((persona) => {
        if (!attivo || !token) return;
        const mio = idPersonaDelToken(token, tokenDiDipendente(token));
        setIsSubscribed(!!persona && (!mio || mio === persona));
      });
    };
    window.addEventListener('online', alRitornoDellaRete);

    return () => {
      attivo = false;
      window.removeEventListener('online', alRitornoDellaRete);
    };
  }, [isSupported, dipendente]);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('associate_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : ''
    };
  }, []);

  /** Iscriviti alle push notifications */
  const subscribe = useCallback(async (): Promise<boolean> => {
    if (!isSupported || !VAPID_PUBLIC_KEY) {
      setError('Push notifications non supportate o VAPID key mancante');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      // 1. Richiedi permesso al browser
      const perm = await Notification.requestPermission();
      setPermission(perm as PushPermissionState);

      if (perm !== 'granted') {
        setError('Permesso notifiche negato dall\'utente');
        setLoading(false);
        return false;
      }

      // 2. Ottieni registrazione SW
      let registration = swRegistrationRef.current;
      if (!registration) {
        registration = await navigator.serviceWorker.register('/sw.js');
        await navigator.serviceWorker.ready;
        swRegistrationRef.current = registration;
      }

      // 3. Crea push subscription
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });

      // 4. Invia subscription al backend
      const token = localStorage.getItem('associate_token');
      let response: Response | null = null;
      let erroreRete: unknown = null;
      try {
        response = await fetch(`${API_BASE_URL}/push/subscribe`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ subscription: subscription.toJSON() })
        });
      } catch (e) {
        erroreRete = e;
      }

      // Dipendente: l'endpoint (che il browser può riusare) non deve restare a un altro se non è passato a lui
      if (token && tokenDiDipendente(token)) {
        const body = response && response.ok ? await response.json().catch(() => null) : null;
        const mio = idDipendenteDi(token);
        if (!mio || !body || !body.success || String(body.dipendente || '') !== mio) {
          await sospendiPushDipendente(token);
          throw new Error('Errore salvataggio subscription sul server');
        }
      }

      if (erroreRete) throw erroreRete;
      if (!response || !response.ok) {
        throw new Error('Errore salvataggio subscription sul server');
      }

      setIsSubscribed(true);
      setLoading(false);
      return true;
    } catch (err: any) {
      setError(err.message || 'Errore attivazione notifiche');
      setLoading(false);
      return false;
    }
  }, [isSupported, getAuthHeaders]);

  /** Disiscriviti dalle push notifications */
  const unsubscribe = useCallback(async (): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      const registration = swRegistrationRef.current || await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        // 1. Rimuovi dal backend
        await fetch(`${API_BASE_URL}/push/unsubscribe`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ endpoint: subscription.endpoint })
        });

        // 2. Rimuovi dal browser
        await subscription.unsubscribe();
      }

      setIsSubscribed(false);
      setLoading(false);
      return true;
    } catch (err: any) {
      setError(err.message || 'Errore disattivazione notifiche');
      setLoading(false);
      return false;
    }
  }, [getAuthHeaders]);

  return { permission, isSubscribed, loading, error, isSupported, subscribe, unsubscribe };
};

export default usePushNotifications;

